import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/apiClient";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Clientes from "@/pages/Clientes";
import ClienteDetalhe from "@/pages/ClienteDetalhe";
import Veiculos from "@/pages/Veiculos";
import VeiculoDetalhe from "@/pages/VeiculoDetalhe";
import KanbanOficina from "@/pages/KanbanOficina";
import BodyShopKanban from "@/pages/BodyShopKanban";
import GarantiasKanban from "@/pages/GarantiasKanban";
import EstimateEditor from "@/pages/EstimateEditor";
import CheckIn from "@/pages/CheckIn";
import CheckStatus from "@/pages/CheckStatus";
import FilaMecanicos from "@/pages/FilaMecanicos";
import Inspecoes from "@/pages/Inspecoes";
import InspecaoDetalhe from "@/pages/InspecaoDetalhe";
import Diagnostico from "@/pages/Diagnostico";
import Agenda from "@/pages/Agenda";
import Arquivados from "@/pages/Arquivados";
import Fechamento from "@/pages/Fechamento";
import FechamentoDetalhe from "@/pages/FechamentoDetalhe";
import Configuracoes from "@/pages/Configuracoes";
import ClientePortal from "@/pages/ClientePortal";
import ClientePortalJob from "@/pages/ClientePortalJob";
import EmBreve from "@/pages/EmBreve";
import AppShell from "@/components/AppShell";
import "@/App.css";

function AuthCallback() {
  const navigate = useNavigate();
  const processed = useRef(false);
  const { checkAuth } = useAuth();

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace("#", ""));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        await api.post("/auth/session", { session_id: sessionId });
        await checkAuth();
        // Clean the hash, go to dashboard
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { replace: true });
      } catch (e) {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground" data-testid="auth-callback">
      <div className="text-sm text-muted-foreground">Autenticando…</div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-sm text-muted-foreground">Carregando…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Rotas permitidas para o perfil Mechanic
const MECHANIC_PATHS = ["/dashboard", "/check-in", "/check-status", "/agenda", "/inspecoes", "/diagnostico", "/fila", "/kanban"];

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/portal/:token" element={<ClientePortal />} />
      <Route path="/portal/:token/job/:jobId" element={<ClientePortalJob />} />
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/clientes" element={<AdminRoute><Clientes /></AdminRoute>} />
        <Route path="/clientes/:id" element={<AdminRoute><ClienteDetalhe /></AdminRoute>} />
        <Route path="/veiculos" element={<AdminRoute><Veiculos /></AdminRoute>} />
        <Route path="/veiculos/:id" element={<AdminRoute><VeiculoDetalhe /></AdminRoute>} />
        <Route path="/kanban" element={<KanbanOficina />} />
        <Route path="/body-shop" element={<AdminRoute><BodyShopKanban /></AdminRoute>} />
        <Route path="/garantias" element={<AdminRoute><GarantiasKanban /></AdminRoute>} />
        <Route path="/estimate/:jobId" element={<AdminRoute><EstimateEditor /></AdminRoute>} />
        <Route path="/check-in" element={<CheckIn />} />
        <Route path="/check-status" element={<CheckStatus />} />
        <Route path="/inspecoes" element={<Inspecoes />} />
        <Route path="/inspecoes/:id" element={<InspecaoDetalhe />} />
        <Route path="/fila" element={<FilaMecanicos />} />
        <Route path="/diagnostico" element={<Diagnostico />} />
        <Route path="/agenda" element={<Agenda />} />
        <Route path="/arquivados" element={<AdminRoute><Arquivados /></AdminRoute>} />
        <Route path="/inventario" element={<AdminRoute><EmBreve titulo="Inventário" /></AdminRoute>} />
        <Route path="/financeiro" element={<AdminRoute><EmBreve titulo="Financeiro" /></AdminRoute>} />
        <Route path="/fechamento" element={<AdminRoute><Fechamento /></AdminRoute>} />
        <Route path="/fechamento/:id" element={<AdminRoute><FechamentoDetalhe /></AdminRoute>} />
        <Route path="/relatorios" element={<AdminRoute><EmBreve titulo="Relatórios" /></AdminRoute>} />
        <Route path="/configuracoes" element={<AdminRoute><Configuracoes /></AdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}
